/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type { CustomerCampaign } from 'embercom/lib/billing/campaign';
import moment from 'moment-timezone';

const FREE_SEATS_WINDOW_DAYS = 30;
export const FREE_SEATS_CAMPAIGN_LOOKUP_KEY = 'campaign_free_seats_offer';

interface FreeSeatsCampaignMetadata {
  automationRate: number;
  finIsLive: boolean;
  subscriptionPhase: 'in_trial' | 'within_free_seats_window' | 'past_free_seats_window';
  currentCampaign?: CustomerCampaign;
}

export default class CompellingOfferCampaignService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentSetupService: $TSFixMe;

  get currentCampaign(): CustomerCampaign | undefined {
    return this.customerService.customer?.currentCampaign;
  }

  get isEnrolled(): boolean {
    return !!this.currentCampaign || this.appService.app.isForcedFreeSeatsCampaignEnrolment;
  }

  get isFreeSeatsCampaign(): boolean {
    return (
      this.currentCampaign?.lookup_key === FREE_SEATS_CAMPAIGN_LOOKUP_KEY ||
      this.appService.app.isForcedFreeSeatsCampaignEnrolment
    );
  }

  /*
    Returns metadata for the free seats campaign, including the current
    subscription phase:
    - in_trial: app is in trial or expired trial
    - within_free_seats_window: app is within FREE_SEATS_WINDOW_DAYS of a paid subscription
    - past_free_seats_window: app is past FREE_SEATS_WINDOW_DAYS of a paid subscription
  */
  get freeSeatsCampaignMetadata(): FreeSeatsCampaignMetadata {
    // TODO: Wire in real automation rate from campaign metadata
    let automationRate = 0.435;
    let finIsLive = !!this.aiAgentSetupService.isFinLive;
    let currentCampaign = this.currentCampaign;

    if (this.cardlessTrialService.inTrialOrExpiredTrial) {
      return { automationRate, finIsLive, subscriptionPhase: 'in_trial', currentCampaign };
    }

    let customer = this.customerService.customer;
    if (!customer) {
      return { automationRate, finIsLive, subscriptionPhase: 'past_free_seats_window' };
    }

    let startedAt = customer.subscription?.startedAt;
    let daysSinceSubscription = moment().diff(startedAt, 'days');

    if (customer.hasActiveSubscription && daysSinceSubscription <= FREE_SEATS_WINDOW_DAYS) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'within_free_seats_window',
        currentCampaign,
      };
    }

    return { automationRate, finIsLive, subscriptionPhase: 'past_free_seats_window' };
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferCampaignService: CompellingOfferCampaignService;
    'compelling-offer-campaign-service': CompellingOfferCampaignService;
  }
}
